/**
 * OC2-3562 — <PrescriptionWork> (renders one bullet for a Prescription Work)
 * Stateless functional component. No useState, no useEffect.
 * GA wiring + URL sync are Phase 3 (composite components).
 */

import { useKontentHomeConfig } from '@/components/contexts/KontentHomeConfigProvider'
import { fetchLinks } from '@/fetchers/fetchLinks'
import type { Mapping } from '@/types'
import type { IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import { useQuery } from '@tanstack/react-query'
import sanitizeHtml from 'sanitize-html'
import type { PrescriptionWork as PrescriptionWorkItem } from './types'

export interface PrescriptionWorkProps {
	work: PrescriptionWorkItem
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
	/**
	 * When true, render the `(type)` label after the title.
	 * Set by:
	 *  - `<PrescriptionGroup>` ← `shouldShowGroupTypeLabel(group, linkedItems)`
	 *    (mixed-type group OR single-work group)
	 *  - `<PrescriptionWorkBundle>` ← always `true`
	 */
	showTypeLabel: boolean
	/**
	 * When true, omit the surrounding `<li>` wrapper so the parent
	 * `<PrescriptionWorkBundle>` can render multiple inline works inside its
	 * single `<li>` (DESIGN_SPEC §5.2.3). Default false.
	 */
	inline?: boolean
}

export default function PrescriptionWork({
	work,
	showTypeLabel,
	inline = false,
}: PrescriptionWorkProps) {
	// Resolve title link target. `title_link` is a modular_content field that
	// accepts link_resource_file / weblinkext / weblinkint — all three are
	// resolved the URL at runtime via `fetchLinks` NOT `getLinkFromLinkUI`
	// because Link Resource Container (link_resource_file) target is a file,
	// absent from sitemap-mappings.json, so the static path returns an empty URL
	// and the title silently falls back to plain text.
	const { preview } = useKontentHomeConfig()
	const titleLinkCodename = work.elements.title_link?.value?.[0] ?? ''
	const { data: titleLinkData } = useQuery({
		queryKey: ['prescription-title-link', titleLinkCodename, preview],
		queryFn: () => fetchLinks({ codenames: [titleLinkCodename], preview }),
		enabled: Boolean(titleLinkCodename),
		staleTime: Infinity,
	})
	const resolvedTitleLink = titleLinkData?.json?.data?.[0]
	const titleLinkUrl = resolvedTitleLink?.url ?? ''
	const isExternalTitleLink = resolvedTitleLink?.type === 'weblinkext'

	const author = work.elements.author.value
	const publication = work.elements.publication.value
	const subtitles = work.elements.subtitles.value
	const typeTerm = work.elements.prescription_type.value[0]
	const trimmedSubtitles = subtitles ? subtitles.trim() : ''

	// Title is a rich-text field but we need it INLINE so author + title sit on
	// the same line. Sanitise to keep only inline emphasis tags (CMS allows
	// italics-only) and drop the wrapping <p> Kontent emits by default.
	const titleHtml = sanitizeHtml(work.elements.title.value ?? '', {
		allowedTags: ['em', 'i', 'strong', 'b'],
		allowedAttributes: {},
	})

	const titleBody = <span dangerouslySetInnerHTML={{ __html: titleHtml }} />

	const titleNode = titleLinkUrl ? (
		<a
			href={titleLinkUrl}
			className="text-nsw-brand-dark"
			{...(isExternalTitleLink
				? { target: '_blank', rel: 'nofollow noopener' }
				: {})}
		>
			{titleBody}
		</a>
	) : (
		titleBody
	)

	const inner = (
		<>
			{author && <>{author}, </>}
			{titleNode}
			{showTypeLabel &&
				typeTerm?.name &&
				` (${typeTerm.name.toLowerCase()})`}
			{publication && (
				<span className="block text-[#767676]">{publication}</span>
			)}
			{trimmedSubtitles && (
				<span className="block pl-4">{subtitles}</span>
			)}
		</>
	)

	if (inline) {
		return (
			<span
				data-testid="prescription-work"
				data-kontent-item-id={work.system.id}
			>
				{inner}
			</span>
		)
	}

	return (
		<li
			data-testid="prescription-work"
			data-kontent-item-id={work.system.id}
		>
			{inner}
		</li>
	)
}
